import React from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';

import SelectBox, { GroupedSelectBox } from '@adsk/bim360-matrix-react-select-box';
import escapeRegexString from '@adsk/bim360-matrix-react-helpers/es/escapeRegexString';
import {
  CREATE_TYPE,
  defaultCreateOptionRenderer,
  defaultFilterOptions
} from '@adsk/bim360-matrix-react-select-box/es/defaultRenderers';

import './_PickBox.scss';

/**
 * PickBox
 *
 * An input container used to pick item from search result. E.g. Used for share documents to specific user.
 * This component is implemented based on SelectBox.
 */
class PickBox extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      // A special logic for PickBox.
      // Show the SelectBox menu only if it have input value for searching purpose
      showMenu: false
    };

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleMenuClose = this.handleMenuClose.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.renderOption = this.renderOption.bind(this);
    this.filterOptions = this.filterOptions.bind(this);
    this.renderComboItem = this.renderComboItem.bind(this);
    this.shouldDisableOption = this.shouldDisableOption.bind(this);
  }

  handleInputChange(value) {
    const inputValue = (value || '').trim();

    const showMenu = !!inputValue; // Only show menu if there is input value.
    if (showMenu !== this.state.showMenu) {
      this.setState({ showMenu });
    }

    if (!showMenu) {
      return;
    }

    this.props.onInputChange(inputValue);
  }

  handleMenuClose() {
    // Force hide the menu if menu closed since it will clear the input value by default
    // but not trigger the input change event.
    if (this.state.showMenu) {
      this.setState({
        showMenu: false
      });
    }
  }

  handleChange(...params) {
    const { combo, onChange } = this.props;

    if (combo) {
      this.handleMenuClose();
    }

    onChange(...params);
  }

  filterOptions(options, inputValue, selectProps) {
    const { filterable, filterOptions = defaultFilterOptions } = this.props;
    if (filterable) {
      return filterOptions(options, inputValue, selectProps);
    } else {
      return options;
    }
  }

  renderHighlightLabel(label, inputValue) {
    if (!inputValue || !label) {
      return label;
    }

    const searchPattern = new RegExp(`(${escapeRegexString(inputValue)})`, 'ig');

    return label.split(searchPattern).map((partial, index) => {
      return (
        <span
          key={index}
          className={cn({
            'PickBox__option--highlighted': partial.toLowerCase() === inputValue.toLowerCase()
          })}
        >
          {partial}
        </span>
      );
    });
  }

  renderOption(option, selected, selectProps) {
    const { labelKey, labelGetter, subtextKey, subtextGetter, stateAndHelpers } = selectProps;
    const { inputValue } = stateAndHelpers;
    const { optionRenderer } = this.props;

    if (optionRenderer) return optionRenderer(option, selected, selectProps);

    const label = labelGetter ? labelGetter(option) : option[labelKey];
    const subtext = subtextGetter ? subtextGetter(option) : option[subtextKey];
    return (
      <div className="PickBox__option-label">
        {this.renderHighlightLabel(label, inputValue)}
        {!!subtext && (
          <div className="PickBox__option-subtext">
            {this.renderHighlightLabel(subtext, inputValue)}
          </div>
        )}
      </div>
    );
  }

  renderComboItem({ item, selected, itemProps, selectProps }) {
    const { createOptionRenderer = defaultCreateOptionRenderer } = this.props;

    if (item[CREATE_TYPE] && !selected) {
      return <li {...itemProps}>{createOptionRenderer(item, selected, selectProps)}</li>;
    }

    return <li {...itemProps}>{this.renderOption(item, selected, selectProps)}</li>;
  }

  shouldDisableOption(option, index, selectProps) {
    const { isOptionDisabled, combo, valueKey } = this.props;

    if (isOptionDisabled) {
      return isOptionDisabled(option, index, selectProps);
    }

    if (combo) {
      const { selectedValue } = selectProps;
      return selectedValue.some(selectedOption => selectedOption[valueKey] === option[valueKey]);
    }

    return false;
  }

  render() {
    const {
      className,
      clearInputOnSelect,
      combo,
      filterable,
      grouped,
      itemRenderer,
      placeholder,
      ...rest
    } = this.props;
    const { showMenu } = this.state;
    const renderItem = itemRenderer || (combo ? this.renderComboItem : undefined);

    const TargetComponent = grouped ? GroupedSelectBox : SelectBox;
    return (
      <TargetComponent
        className={cn(className, 'PickBox', {
          'PickBox--no-menu': !showMenu
        })}
        {...rest}
        clearInputOnSelect={combo || clearInputOnSelect}
        filterOptions={this.filterOptions}
        filterable={filterable}
        isOptionDisabled={this.shouldDisableOption}
        itemRenderer={renderItem}
        multiple
        onChange={this.handleChange}
        onClose={this.handleMenuClose}
        onInputChange={this.handleInputChange}
        placeholder={placeholder}
        searchable
        searchPlaceholder={placeholder} // Use placeholder for search
        showValueAsTags
      />
    );
  }
}

PickBox.defaultProps = {
  ...SelectBox.defaultProps
};

PickBox.propTypes = {
  ...SelectBox.propTypes,
  /**
   * Whether the options are grouped
   */
  grouped: PropTypes.bool,
  /**
   * Whether to use the component in combo mode
   */
  combo: PropTypes.bool
};

export default PickBox;
